import * as Sentry from '@sentry/react'
import classNames from 'classnames'
import { type SerializedEditorState } from 'lexical'
import { type FC, type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Col } from 'react-bootstrap'
import { flushSync } from 'react-dom'
import { useAccount } from '../../../../common/useAccount'
import { useUnsavedChangesGuard } from '../../../../common/useUnsavedChangesGuard'
import { UnsavedChangesModal } from '../../../../components/Modal/UnsavedChangesModal'
import { type LexicalEmailTemplate, type TemplateType, type TemplateValue } from '../../../../models/TemplateLibrary'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { TemplateEditorBoxControls, TemplateEditorDisabledBanner, UnopenableTemplateBox } from '../TemplateEditorBox'
import { disabledBannerTextFor, isOwnedByAnotherUser, OWNED_BY_ANOTHER_USER_TEXT, templateSaveNotices } from '../templateEditorMessages'
import { type SaveBarNotice, withoutSuccess } from '../../../../components/SaveBarNotice/saveBarNotice'
import { type CompiledEmail, compileEmail, tryCompileEmail } from './compile/compile'
import { EditPreviewToggle, type EditorMode } from './EditPreviewToggle'
import { EmailEditor } from './EmailEditor'
import styles from './EmailEditor.module.scss'
import { EmailEnvelope } from './EmailEnvelope'
import { EmailIssues } from './EmailIssues'
import { EmailPreview } from './EmailPreview'
import { EmailCanvas } from './EmailPreviewCard'
import { LEXICAL_EMAIL_FORMAT } from './emailTemplateFormat'
import { CHIP_CLASSES } from './nodes/VariableNode'
import { renderPreview, sampleSenderName, samplesFor } from './preview/renderPreview'
import { validateCompiled } from './validate/jinjaAllowlist'
import { issueNumbers, issueSummary } from './validate/issueSummary'
import { checkRecord } from './validate/recordChecks'
import { emailOf, type StateError, validateState } from './validate/validateState'

const LOCKED_MESSAGE = "Locked parts can't be changed. That edit was undone."
const SYSTEM_NOTICE = 'Changes are saved as a new template. The Zocks default stays as it is.'
const COMPILED_MESSAGE = "Some text can't be sent. Remove any {{, }}, {% or {# from it."
const UNCOMPILABLE_MESSAGE = "This email can't be saved as it is. Undo your last change or discard your changes."

export interface EmailTemplateEditorBoxProps {
  templateValue: TemplateValue
  readOnly: boolean
  showDisabledBanner: boolean
  onSave: (template: TemplateType) => Promise<void> | void
  onRequestDeferredSave: (template: TemplateType) => void
  loading: boolean
  bypassBlockerRef: MutableRefObject<boolean>
}

interface Baseline {
  state: SerializedEditorState
  compiled: CompiledEmail
}

const sameCompiled = (a: CompiledEmail, b: CompiledEmail): boolean => a.subject === b.subject && a.body === b.body

const LoadedEmailTemplateEditorBox: FC<EmailTemplateEditorBoxProps & { record: LexicalEmailTemplate }> = ({
  templateValue,
  record,
  readOnly,
  showDisabledBanner,
  onSave,
  onRequestDeferredSave,
  loading,
  bypassBlockerRef,
}) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { account } = useAccount()
  const [baseline, setBaseline] = useState<Baseline>(() => ({ state: record.editor.state, compiled: compileEmail(record.editor.state) }))
  const [state, setState] = useState<SerializedEditorState>(record.editor.state)
  const [discardCount, setDiscardCount] = useState(0)
  const [mode, setMode] = useState<EditorMode>('edit')
  const [notice, setNotice] = useState<SaveBarNotice | null>(null)
  const [wasRejected, setWasRejected] = useState(false)
  const [systemNoticeDismissed, setSystemNoticeDismissed] = useState(false)
  const [toolbarContainer, setToolbarContainer] = useState<HTMLElement | null>(null)
  const [canvas, setCanvas] = useState<HTMLElement | null>(null)

  const isSystem = templateValue.scope === 'system'
  const variables = useMemo(() => emailOf(state)?.variables ?? [], [state])
  const compileResult = useMemo(() => tryCompileEmail(state), [state])
  const compiled = compileResult.ok ? compileResult.email : null
  const reportedCompileErrors = useRef(new Set<string>())
  const reportedRenderErrors = useRef(new Set<string>())
  const errors: StateError[] = useMemo(() => {
    const stateErrors = validateState(state)
    if (stateErrors.length > 0) {
      return stateErrors
    }
    if (compiled === null) {
      return [{ slotId: '', message: UNCOMPILABLE_MESSAGE, kind: 'content' }]
    }
    const unsendable =
      validateCompiled(
        compiled.subject,
        compiled.body,
        variables.map((variable) => variable.name),
      ).length > 0
    return unsendable ? [{ slotId: '', message: COMPILED_MESSAGE, kind: 'content' }] : []
  }, [state, compiled, variables])
  const numbers = useMemo(() => issueNumbers(errors), [errors])
  const isDirty = !readOnly && (compiled === null || !sameCompiled(compiled, baseline.compiled))
  const canSave = isDirty && errors.length === 0
  const samples = useMemo(() => samplesFor(variables), [variables])
  const rendered = useMemo(
    () =>
      mode === 'preview' && compiled && errors.length === 0
        ? renderPreview(
            compiled,
            samples,
            variables.map((variable) => variable.name),
          )
        : null,
    [mode, compiled, errors, samples, variables],
  )

  const issueStatus = wasRejected ? null : issueSummary(errors)
  const statusText = wasRejected ? LOCKED_MESSAGE : issueStatus ? issueStatus.status : isDirty ? 'Unsaved changes' : undefined

  const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty, false, bypassBlockerRef)

  useEffect(() => {
    if (!compileResult.ok && !reportedCompileErrors.current.has(compileResult.message)) {
      reportedCompileErrors.current.add(compileResult.message)
      Sentry.captureException(new Error('Email template did not compile'), { extra: { templateId: templateValue.id, message: compileResult.message } })
    }
  }, [compileResult, templateValue.id])

  useEffect(() => {
    const renderError = rendered?.error ?? null
    if (renderError !== null && !reportedRenderErrors.current.has(renderError)) {
      reportedRenderErrors.current.add(renderError)
      Sentry.captureException(new Error('Email template preview failed'), { extra: { templateId: templateValue.id, message: renderError } })
    }
  }, [rendered, templateValue.id])

  const handleSave = async (): Promise<boolean> => {
    if (!compiled) {
      return false
    }
    const template: LexicalEmailTemplate = {
      format: LEXICAL_EMAIL_FORMAT,
      schemaVersion: 1,
      subject: compiled.subject,
      body: compiled.body,
      editor: { ...record.editor, state },
    }
    if (isSystem) {
      onRequestDeferredSave(template)
      return false
    }
    try {
      await onSave(template)
    } catch (error) {
      setNotice(templateSaveNotices.saveFailed(error))
      return false
    }
    flushSync(() => {
      setBaseline({ state, compiled })
    })
    setNotice(templateSaveNotices.saved())
    return true
  }

  const handleModalSave = async () => {
    if (await handleSave()) {
      confirmLeave()
      return
    }
    cancelLeave()
  }

  const handleDiscard = () => {
    setState(baseline.state)
    setWasRejected(false)
    setNotice(null)
    setDiscardCount((count) => count + 1)
  }

  const handleChange = (next: SerializedEditorState) => {
    setState(next)
    setWasRejected(false)
    setNotice(withoutSuccess)
  }

  return (
    <>
      <Col className='d-flex flex-column h-100'>
        <div className={classNames('template-library-box d-flex flex-column flex-grow-1 h-100', styles.fillViewport)}>
          <div className='d-flex flex-column flex-grow-1 gap-3 overflow-y-auto zocks-p-2.0'>
            {showDisabledBanner ? <TemplateEditorDisabledBanner text={disabledBannerTextFor(templateValue, account)} /> : null}
            {isSystem && !systemNoticeDismissed ? (
              <Alert
                variant='light'
                dismissible
                onClose={() => setSystemNoticeDismissed(true)}
                className='mb-0 d-flex align-items-center gap-2 paragraph-small zocks-text-gray-900 border zocks-border-color-gray-200 zocks-bg-color-gray-100 zocks-border-0.75'
              >
                <i className='bi bi-info-circle zocks-text-gray-600' aria-hidden='true' />
                {SYSTEM_NOTICE}
              </Alert>
            ) : null}
            <EmailIssues errors={errors} />
            <div ref={setToolbarContainer} />
            <div className='d-flex align-items-center justify-content-between px-3'>
              <div className='d-flex align-items-center gap-3 paragraph-small zocks-text-gray-600'>
                <span className='d-flex align-items-center zocks-gap-0.375'>
                  <span className={styles.legendSwatch} />
                  You can edit
                </span>
                <span className='d-flex align-items-center zocks-gap-0.375'>
                  <i className='bi bi-lock' aria-hidden='true' />
                  Locked
                </span>
                <span className='d-flex align-items-center zocks-gap-0.375'>
                  <i className='bi bi-braces' aria-hidden='true' />
                  Type {'{'} to insert a variable
                </span>
              </div>
              <EditPreviewToggle mode={mode} onChange={setMode} />
            </div>
            <EmailCanvas ref={setCanvas} className={classNames(styles.linkAnchor, { 'd-none': mode === 'preview' })}>
              <div className='d-flex flex-column'>
                <EmailEnvelope joined fromName={<span className={classNames(CHIP_CLASSES)}>Advisor name</span>} />
                <EmailEditor
                  key={`${templateValue.id}:${discardCount}`}
                  initialState={state}
                  variables={variables}
                  issueNumbers={numbers}
                  readOnly={readOnly}
                  toolbarDisabled={mode === 'preview'}
                  toolbarContainer={toolbarContainer}
                  floatingAnchor={canvas}
                  onChange={handleChange}
                  onRejected={() => setWasRejected(true)}
                />
              </div>
            </EmailCanvas>
            {mode === 'preview' ? (
              <EmailPreview rendered={rendered} fromName={sampleSenderName(samples)} hasIssues={errors.length > 0} onEdit={() => setMode('edit')} />
            ) : null}
          </div>
          <TemplateEditorBoxControls
            onDiscard={handleDiscard}
            onSave={() => void handleSave()}
            loading={loading}
            disabled={!canSave}
            discardDisabled={!isDirty}
            notice={notice}
            onDismissNotice={() => setNotice(null)}
            saveLabel={isSystem ? 'Save as new template' : 'Save'}
            statusText={statusText}
            statusTone={issueStatus ? 'error' : 'neutral'}
            infoText={isOwnedByAnotherUser(templateValue, currentUserId) ? OWNED_BY_ANOTHER_USER_TEXT : undefined}
          />
        </div>
      </Col>
      {isBlocked ? (
        <UnsavedChangesModal
          show
          onHide={cancelLeave}
          onSave={() => void handleModalSave()}
          onDiscard={confirmLeave}
          saveDisabled={errors.length > 0}
          hideSave={isSystem}
          loading={loading}
        />
      ) : null}
    </>
  )
}

export const EmailTemplateEditorBox: FC<EmailTemplateEditorBoxProps> = (props) => {
  const { templateValue } = props
  const parsed = useMemo(() => checkRecord(templateValue.template), [templateValue.template])
  useEffect(() => {
    if (parsed.kind === 'invalid') {
      Sentry.captureException(new Error("Email template can't be opened"), { extra: { templateId: templateValue.id, reason: parsed.reason } })
    }
  }, [parsed, templateValue.id])
  if (parsed.kind === 'email') {
    return <LoadedEmailTemplateEditorBox {...props} record={parsed.record} />
  }
  return <UnopenableTemplateBox />
}
